import type { ReactElement } from 'react';
import { X } from 'lucide-react';
import { Button, Dialog, IconButton } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export type ArchiveDeleteTarget =
  | { readonly type: 'card'; readonly id: string; readonly title: string }
  | {
      readonly type: 'list';
      readonly id: string;
      readonly title: string;
      readonly cardCount: number;
    };

export interface ArchiveDeleteConfirmProps {
  readonly target: ArchiveDeleteTarget | null;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ArchiveDeleteConfirm({
  target,
  busy,
  onConfirm,
  onCancel,
}: ArchiveDeleteConfirmProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  if (target === null) return null;

  /* The blank-title fallback, resolved INLINE rather than through the old
     module-level `targetName` helper. `t` is only reachable here, and a helper
     returning the key would make both keys invisible to a `t('…')` call-site
     scan — the `workspaceSwitcher.allWorkspaces` hazard, taken only where a
     module constant leaves no choice. */
  const name =
    target.title.trim() !== ''
      ? target.title
      : target.type === 'list'
        ? t('boardAdmin.archiveDelete.untitledList')
        : t('boardAdmin.archiveDelete.untitledCard');
  const isList = target.type === 'list';
  const title = isList
    ? t('boardAdmin.archiveDelete.title.list')
    : t('boardAdmin.archiveDelete.title.card');
  /* ⚠ BOTH ARGUMENT CHANNELS on the list arm. `interfaceMessage` throws on an
     unfilled `{slot}`, so `{ name }` alone throws on every list whose card count
     is not 1 — inside a destructive confirmation. The count travels twice: once
     as a value for `{count}`, once as the plural selector. */
  const body = isList
    ? t(
        'boardAdmin.archiveDelete.body.list',
        { name, count: target.cardCount },
        target.cardCount,
      )
    : t('boardAdmin.archiveDelete.body.card', { name });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      size="sm"
      title={title}
      description={body}
      closeControl={
        <IconButton label={t('boardAdmin.archiveDelete.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('boardAdmin.archiveDelete.cancel')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            busy={busy}
            busyLabel={t('boardAdmin.archiveDelete.busy')}
            onClick={onConfirm}
          >
            {t('boardAdmin.archiveDelete.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">{t('boardAdmin.archiveDelete.note')}</p>
    </Dialog>
  );
}
